/**
 * Letter Never Sent (v2).
 *
 * A private place to write what cannot be said. A letter has three lives:
 *   Draft     still being written      (edit, seal, release)
 *   Sealed    locked with a passphrase (unlock, edit, release)
 *   Released  symbolically let go      (nothing is sent; a private copy may be kept)
 *
 * All letters are saved on the server (see backend/controllers/letterController.js),
 * which also enforces the rules and the Free-plan limit. This file is only the screens.
 *
 * HOW THIS FILE IS ORGANISED (top to bottom):
 *   1. Words        every piece of text the screens show, in one place
 *   2. Choices      papers, fonts and "who is it for" options
 *   3. Server calls small functions that talk to the backend
 *   4. Old letters  moves letters saved by the first version into the account
 *   5. Styles       one stylesheet, with a dark and a light theme
 *   6. Building blocks   Dialog, release animation, envelope picture
 *   7. Screens      My Letters, Studio (writing), Reader, Released
 *   8. LetterMode   the main component that decides which screen to show
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Lock, PenLine, Sparkles, Trash2 } from "lucide-react";
import { api } from "../../services/api";

/* ═════════════════════════════════════════════════════════════════════════
   1. WORDS
   Every visible sentence lives here. Each one can be translated by adding a
   key called "lt_<name>" under "astria" in the language files; until then
   the English below is shown.
   ═════════════════════════════════════════════════════════════════════════ */
const TEXT = {
  eyebrow: "A LETTER NEVER SENT",
  title: "A Letter Never Sent",
  subtitle: "Some words are easier to write than to say.",
  steps: "Draft them. Seal them. Release them when you're ready.",
  private: "Private. Always yours.",
  myLetters: "My Letters",
  back: "Back",
  newLetter: "New Letter",
  newLine: "New line",
  writeLetter: "Write a Letter",
  planUsage: "{{used}} of {{max}} letters in use on your Free plan",
  loading: "Opening your letters…",
  loadFailed: "We couldn't open your letters just now.",
  retry: "Try again",
  oldMoved: "Your earlier letters were moved into your account.",
  sealedNotice: "Your letter is sealed and kept private.",

  emptyTitle: "Your first letter begins here.",
  emptyText: "A quiet place for the words you carry.",

  draftsTitle: "Drafts",
  draftsHint: "Letters still in progress",
  sealedTitle: "Sealed",
  sealedHint: "Protected by passphrase",
  keptTitle: "Kept Letters",
  keptHint: "Letters you keep close",
  releasedTitle: "Released",
  releasedHint: "Words you've acknowledged and chosen to let go",

  untitled: "Untitled Letter",
  stillInProgress: "Still in progress",
  sealedOn: "Sealed {{date}}",
  releasedOn: "Released {{date}}",
  protectedBy: "Protected by passphrase",
  needsUnlock: "Unlock to move this letter into your account",
  noCopy: "Released without a copy",
  deleteLetter: "Delete this letter",
  to: "To {{name}}",

  titlePlaceholder: "Untitled Letter",
  whoFor: "Who is this letter for?",
  toLabel: "To",
  namePlaceholder: "Their name (optional)",
  bodyPlaceholder: "Dear you...",
  paper: "Paper",
  font: "Handwriting",
  saving: "Saving...",
  saved: "Saved privately",
  saveFailed: "Unable to save",

  saveForLater: "Save for Later",
  sealAndKeep: "Seal & Keep",
  saveSealed: "Save & Keep Sealed",
  releaseLetter: "Release Letter",
  editLetter: "Reopen for Editing",

  sealTitle: "Seal this letter",
  sealText: "Choose a passphrase. Only it can open this letter. If you forget it, we cannot recover the letter for you.",
  passphrase: "Passphrase",
  passphraseAgain: "Type it again",
  sealNow: "Seal Letter",
  passphraseMismatch: "The two passphrases don't match.",

  unlockTitle: "Unlock this letter",
  unlockText: "Enter the passphrase you chose when you sealed it.",
  unlock: "Unlock",

  releaseTitle: "Release this letter?",
  releaseText:
    "Releasing a letter symbolizes letting go of what you've been carrying. No message will be sent to anyone, and a released letter can no longer be edited or sealed.",
  keepCopy: "Keep a private copy for me",
  keepCopyHint: "You can read it later in My Letters, and erase it whenever you like.",
  notYet: "Not yet",
  releaseFailed: "We couldn't release this letter. Nothing was changed, so you can try again.",

  deleteTitle: "Delete this letter?",
  deleteText: "This permanently removes the letter and its words. It can't be undone.",
  deleteNow: "Delete permanently",
  keepIt: "Keep it",
  eraseTitle: "Erase your copy?",
  eraseText: "The words will be permanently deleted. The moment you released this letter will remain.",
  eraseNow: "Erase permanently",
  eraseCopy: "Erase my copy",
  noCopyText: "This letter was released without a copy. Only the moment remains.",

  limitTitle: "Your Free plan includes one letter",
  limitText: "Release or delete your current letter to write another, or see the plans for unlimited letters.",
  seePlans: "See plans",
  close: "Close",

  releasedEyebrow: "A MOMENT FOR YOURSELF",
  releasedHeading: "Your words have been released.",
  releasedMessage: "You gave these feelings a place to exist. You can leave them here.",
  releasedNote: "No message was sent to anyone.",
  returnToJourney: "Return to Journey",

  errWrongPass: "That passphrase doesn't open this letter.",
  errLocked: "Too many attempts. Please try again in {{minutes}} minutes.",
  errBadPass: "Please use a passphrase of 4 to 128 characters.",
  errEmpty: "Write something first.",
  errTooLong: "This letter is too long to save.",
  errNetwork: "We couldn't reach the server. Please check your connection.",
  errGeneric: "Something went wrong. Please try again.",
};
type TextKey = keyof typeof TEXT;
type Copy = (key: TextKey, values?: Record<string, string | number>) => string;

/* ═════════════════════════════════════════════════════════════════════════
   2. CHOICES
   The ids below are what the server stores, so do not rename them.
   ═════════════════════════════════════════════════════════════════════════ */
const PAPERS = [
  { id: "soft_ivory", name: "Soft Ivory", paper: "#F7F1E6", ink: "#3A3128" },
  { id: "moon_white", name: "Moon White", paper: "#FAF9F6", ink: "#2B2B33" },
  { id: "dusty_rose", name: "Dusty Rose", paper: "#EEE1E2", ink: "#3D2B30" },
  { id: "soft_sage", name: "Soft Sage", paper: "#DFE6DF", ink: "#2C3A31" },
  { id: "midnight", name: "Midnight", paper: "#252536", ink: "#ECEAF5" },
];

// Each stack ends with fonts that can draw Thai, so Thai letters never fall back to a random font.
const FONTS = [
  { id: "classic_serif", name: "Classic Serif", family: "'Lora','Noto Serif Thai',Georgia,serif", scale: 1 },
  { id: "modern_sans", name: "Modern Sans", family: "'Inter','Noto Sans Thai',system-ui,sans-serif", scale: 0.95 },
  { id: "personal_script", name: "Personal Script", family: "'Caveat','Mali','Segoe Script',cursive", scale: 1.3 },
];
const FONT_LINK_ID = "astria-letter-fonts";
const FONT_LINK_HREF =
  "https://fonts.googleapis.com/css2?family=Lora:wght@400;500&family=Caveat:wght@500&family=Mali:wght@400&family=Noto+Serif+Thai:wght@400&display=swap";

const RECIPIENTS = [
  { id: "love", name: "Someone I Love" },
  { id: "family", name: "Family" },
  { id: "friend", name: "Friend" },
  { id: "miss", name: "Someone I Miss" },
  { id: "younger_self", name: "My Younger Self" },
  { id: "future_self", name: "My Future Self" },
  { id: "forgive", name: "Someone I Forgive" },
  { id: "myself", name: "Myself" },
  { id: "other", name: "Other" },
  { id: "anonymous", name: "Anonymous" },
];

const paperOf = (id: string) => PAPERS.find((p) => p.id === id) || PAPERS[0];
const fontOf = (id: string) => FONTS.find((f) => f.id === id) || FONTS[0];
const recipientOf = (id: string) => RECIPIENTS.find((r) => r.id === id) || RECIPIENTS[8];

/** Colours and font of a letter's paper, used by cards, the writing page and the reader. */
const paperStyle = (paperId: string, fontId: string): React.CSSProperties => {
  const p = paperOf(paperId);
  const f = fontOf(fontId);
  return { background: p.paper, color: p.ink, fontFamily: f.family, ["--lt-font-scale" as string]: f.scale };
};

/* ═════════════════════════════════════════════════════════════════════════
   3. SERVER CALLS
   ═════════════════════════════════════════════════════════════════════════ */
type Status = "draft" | "sealed" | "kept" | "released";

interface Letter {
  id: string;
  title: string;
  recipientType: string;
  recipientName: string;
  paper: string;
  font: string;
  status: Status;
  retainedCopy: boolean;
  updatedAt: string;
  sealedAt: string | null;
  releasedAt: string | null;
  /** First words of an open letter. Always null for sealed letters. */
  preview: string | null;
  /** The full text. Only present after a letter has been opened or unlocked. */
  content?: string;
  /** Set for a letter that still lives only in this browser (see section 4). */
  oldId?: string;
}
interface Limit {
  max: number | null; // null = no limit
  used: number;
}
type Fields = Partial<Pick<Letter, "title" | "recipientType" | "recipientName" | "paper" | "font">> & {
  content?: string;
};

const BASE = "/backend/letters";
const listLetters = () => api.get<{ letters: Letter[]; limit: Limit }>(BASE).then((r) => r.data);
const createLetter = (fields: Fields) => api.post<{ letter: Letter }>(BASE, fields).then((r) => r.data.letter);
const saveLetter = (id: string, fields: Fields & { passphrase?: string }) =>
  api.put<{ letter: Letter }>(`${BASE}/${id}`, fields).then((r) => r.data.letter);
const sealLetter = (id: string, passphrase: string) =>
  api.post<{ letter: Letter }>(`${BASE}/${id}/seal`, { passphrase }).then((r) => r.data.letter);
const unlockLetter = (id: string, passphrase: string) =>
  api.post<{ letter: Letter }>(`${BASE}/${id}/unlock`, { passphrase }).then((r) => r.data.letter);
const openLetter = (id: string) => api.get<{ letter: Letter }>(`${BASE}/${id}`).then((r) => r.data.letter);
const releaseLetter = (id: string, body: { retainCopy: boolean; passphrase?: string }) =>
  api.post<{ letter: Letter }>(`${BASE}/${id}/release`, body).then((r) => r.data.letter);
const deleteLetter = (id: string) => api.delete(`${BASE}/${id}`, { params: { confirm: "true" } });
const eraseCopy = (id: string) => api.delete(`${BASE}/${id}/copy`, { data: { confirm: true } });

/** Turns a failed request into a sentence the person can read. Never includes letter text. */
const explain = (err: unknown, copy: Copy): string => {
  const data = (err as { response?: { data?: { code?: string; retryAfterSec?: number } } })?.response?.data;
  switch (data?.code) {
    case "WRONG_PASSPHRASE":
      return copy("errWrongPass");
    case "LOCKED":
      return copy("errLocked", { minutes: Math.max(1, Math.ceil((data.retryAfterSec || 60) / 60)) });
    case "BAD_PASSPHRASE":
      return copy("errBadPass");
    case "EMPTY":
      return copy("errEmpty");
    case "TOO_LONG":
      return copy("errTooLong");
    case undefined:
      return (err as { response?: unknown })?.response ? copy("errGeneric") : copy("errNetwork");
    default:
      return copy("errGeneric");
  }
};
const isLimitError = (err: unknown) =>
  (err as { response?: { data?: { code?: string } } })?.response?.data?.code === "LIMIT_REACHED";

/* ═════════════════════════════════════════════════════════════════════════
   4. OLD LETTERS
   The first version kept letters only in this browser. On opening, drafts and
   released markers are moved into the account and removed from the browser.
   Old sealed letters are encrypted with the old method, so they move when the
   person unlocks them.
   ═════════════════════════════════════════════════════════════════════════ */
const OLD_KEY = "astria_letters_v1";

interface OldLetter {
  id: string;
  status: "draft" | "sealed" | "released";
  category: string;
  paper: string;
  font: string;
  updatedAt: number;
  text: string;
  vault?: { salt: string; iv: string; data: string };
}

const readOldLetters = (): OldLetter[] => {
  try {
    const parsed = JSON.parse(localStorage.getItem(OLD_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};
const removeOldLetters = (ids: string[]) => {
  try {
    const left = readOldLetters().filter((l) => !ids.includes(l.id));
    if (left.length) localStorage.setItem(OLD_KEY, JSON.stringify(left));
    else localStorage.removeItem(OLD_KEY);
  } catch {
    /* storage blocked: nothing to clean up */
  }
};
const oldToImport = (l: OldLetter, extra: Record<string, unknown> = {}) => ({
  legacyId: l.id,
  status: l.status,
  text: l.text || "",
  category: l.category,
  paper: l.paper,
  font: l.font,
  updatedAt: l.updatedAt,
  ...extra,
});

/** Moves old drafts and released markers to the account. Returns how many moved. */
const moveOldLettersToAccount = async (): Promise<number> => {
  const movable = readOldLetters().filter((l) => l.status !== "sealed");
  let moved = 0;
  for (let i = 0; i < movable.length; i += 10) {
    const batch = movable.slice(i, i + 10);
    const { data } = await api.post<{ imported: string[]; skipped: string[] }>(`${BASE}/import`, {
      letters: batch.map((l) => oldToImport(l)),
    });
    // "skipped" means already moved before, or empty: either way it is safe to drop locally.
    removeOldLetters([...data.imported, ...data.skipped]);
    moved += data.imported.length;
  }
  return moved;
};

const b64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
/** Opens an old (browser-encrypted) sealed letter. Throws when the passphrase is wrong. */
const decryptOldLetter = async (old: OldLetter, passphrase: string): Promise<string> => {
  if (!old.vault) throw new Error("no vault");
  const base = await crypto.subtle.importKey("raw", new TextEncoder().encode(passphrase), "PBKDF2", false, ["deriveKey"]);
  const key = await crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: b64(old.vault.salt), iterations: 150000, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"],
  );
  const plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: b64(old.vault.iv) }, key, b64(old.vault.data));
  return new TextDecoder().decode(plain);
};

/** Shows an old sealed letter in the Sealed list, like any other card. */
const oldSealedAsLetter = (old: OldLetter): Letter => ({
  id: `old_${old.id}`,
  oldId: old.id,
  title: "",
  recipientType: "other",
  recipientName: "",
  paper: "soft_ivory",
  font: "classic_serif",
  status: "sealed",
  retainedCopy: false,
  updatedAt: new Date(old.updatedAt).toISOString(),
  sealedAt: new Date(old.updatedAt).toISOString(),
  releasedAt: null,
  preview: null,
});

/* ═════════════════════════════════════════════════════════════════════════
   5. STYLES
   Colours are variables. The dark set is the default; the light set switches
   on when the app theme is light. Only .lt (this feature) is affected.
   ═════════════════════════════════════════════════════════════════════════ */
const STYLES = `
.lt{
  --lt-bg:#1D1724; --lt-surface:#292238; --lt-text:#F5F0F2; --lt-muted:#B9AABB;
  --lt-accent:#B6A3CC; --lt-accent-soft:rgba(182,163,204,.16); --lt-accent-ink:#1D1724; --lt-line:rgba(245,240,242,.14);
  --lt-danger:#E0837F; --lt-shadow:0 14px 36px rgba(0,0,0,.38);
  --lt-heading:'Cormorant Garamond','Noto Serif Thai',Georgia,serif;
  --lt-ui:Inter,'Noto Sans Thai',system-ui,sans-serif;
  --lt-env:#E9DFC9; --lt-env-front:#F1E9D6; --lt-env-flap:#F5EEDD; --lt-env-open:#DCCFB4;
  position:absolute; inset:0; z-index:60; display:flex; flex-direction:column;
  background:var(--lt-bg); color:var(--lt-text); font-family:var(--lt-ui);
}
html[data-theme="light"] .lt{
  --lt-bg:#F6F1EC; --lt-surface:#FFFFFF; --lt-text:#2A2230; --lt-muted:#6B5F72;
  --lt-accent:#7D6A99; --lt-accent-soft:rgba(125,106,153,.12); --lt-accent-ink:#FFFFFF; --lt-line:rgba(42,34,48,.14);
  --lt-danger:#B5483F; --lt-shadow:0 10px 28px rgba(70,50,80,.14);
  --lt-env:#E2D6BC; --lt-env-front:#ECE2CB; --lt-env-flap:#F1E8D2; --lt-env-open:#D3C5A7;
}
.lt{-webkit-text-size-adjust:100%;text-size-adjust:100%}
.lt *{box-sizing:border-box}
.lt button{font-family:inherit;cursor:pointer}
.lt :focus-visible{outline:2px solid var(--lt-accent);outline-offset:2px}

/* top bar and page */
.lt-top{flex:none;display:grid;grid-template-columns:44px 1fr 44px;align-items:center;height:60px;padding:0 12px}
.lt-top__title{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;margin:0;text-align:center;font-family:var(--lt-heading);font-size:20px;letter-spacing:.02em}
@media (max-width:1023px){.lt-top{padding:0 12px 0 60px}.lt-top__title{font-size:18px}}
.lt-iconbtn{width:44px;height:44px;display:grid;place-items:center;border:0;border-radius:50%;background:transparent;color:var(--lt-muted)}
.lt-iconbtn:hover{color:var(--lt-text);background:var(--lt-line)}
.lt-scroll{flex:1;min-height:0;overflow-y:auto;padding:8px 20px 56px}
.lt-view{animation:lt-in .45s ease backwards}
.lt-wide{max-width:980px;margin:0 auto}
.lt-narrow{max-width:760px;margin:0 auto}
@keyframes lt-in{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}

/* text */
.lt-eyebrow{font-size:11px;letter-spacing:.3em;color:var(--lt-accent)}
.lt-h1{font-family:var(--lt-heading);font-weight:500;font-size:clamp(30px,5vw,44px);line-height:1.15;margin:10px 0 12px}
.lt-muted{color:var(--lt-muted);line-height:1.7}
.lt-hero{text-align:center;padding:12px 0 8px}
.lt-hero .lt-muted{max-width:480px;margin:0 auto}
.lt-error{color:var(--lt-danger);font-size:14px;margin:10px 0 0}

/* buttons */
.lt-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;min-height:44px;padding:0 22px;border-radius:12px;border:1px solid var(--lt-line);background:transparent;color:var(--lt-text);font-size:15px;transition:background .2s,filter .2s}
.lt-btn:hover:not(:disabled){background:var(--lt-line)}
.lt-btn--primary{background:var(--lt-accent);border-color:var(--lt-accent);color:var(--lt-accent-ink);font-weight:600}
.lt-btn--primary:hover:not(:disabled){background:var(--lt-accent);filter:brightness(1.07)}
.lt-btn--text{border-color:transparent;color:var(--lt-muted)}
.lt-btn--danger{color:var(--lt-danger);border-color:transparent}
.lt-btn:disabled{opacity:.5;cursor:default}
.lt-actions{display:flex;flex-wrap:wrap;justify-content:center;gap:12px;margin-top:8px}
@media (max-width:560px){.lt-actions .lt-btn{flex:1 1 100%}}

/* My Letters */
.lt-section{margin-top:40px}
.lt-section__head{display:flex;flex-wrap:wrap;align-items:baseline;gap:4px 12px;margin-bottom:16px;padding-bottom:10px;border-bottom:1px solid var(--lt-line)}
.lt-section__title{display:flex;align-items:center;gap:8px;margin:0;font-family:var(--lt-heading);font-weight:600;font-size:23px}
.lt-section__hint{font-size:13px;color:var(--lt-muted)}
.lt-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(250px,1fr));gap:18px}
.lt-card{position:relative;display:flex;min-height:196px;border-radius:6px 6px 14px 14px;box-shadow:var(--lt-shadow);transition:transform .3s ease;background-image:linear-gradient(180deg,rgba(255,255,255,.4),rgba(255,255,255,0) 45%)}
.lt-card:hover{transform:translateY(-3px)}
.lt-card__main{all:unset;box-sizing:border-box;display:flex;flex-direction:column;flex:1;width:100%;padding:20px 20px 16px;cursor:pointer;border-radius:inherit}
.lt-card__main:focus-visible{outline:2px solid var(--lt-accent);outline-offset:3px}
.lt-card__title{font-size:calc(21px * var(--lt-font-scale,1));font-weight:600;line-height:1.2;padding-right:30px;overflow-wrap:anywhere}
.lt-card__meta{margin-top:4px;font-size:12px;opacity:.7}
.lt-card__body{margin-top:12px;font-size:calc(14px * var(--lt-font-scale,1));line-height:1.6;opacity:.85;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.lt-card__sealed{display:flex;align-items:center;gap:8px;margin-top:14px;font-size:13px;opacity:.8}
.lt-card__foot{margin-top:auto;padding-top:14px;font-size:12px;opacity:.75}
.lt-card__delete{position:absolute;top:6px;right:6px;width:44px;height:44px;display:grid;place-items:center;border:0;border-radius:50%;background:transparent;color:inherit;opacity:.5}
.lt-card__delete:hover{opacity:1;background:rgba(128,128,128,.18)}
.lt-empty{text-align:center;padding:56px 0 24px}
.lt-notice{margin:18px auto 0;max-width:460px;padding:10px 16px;border-radius:12px;background:var(--lt-surface);border:1px solid var(--lt-line);text-align:center;font-size:14px}

/* Studio (writing) and Reader */
.lt-bar{display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:4px 12px;margin:4px 0 16px;min-height:24px}
.lt-status{min-width:0;margin-left:auto;font-size:13px;text-align:right;color:var(--lt-muted)}
.lt-paper{border-radius:6px 6px 14px 14px;padding:clamp(24px,5vw,52px);box-shadow:var(--lt-shadow),inset 0 0 0 1px rgba(255,255,255,.28);transition:background .35s,color .35s;background-image:linear-gradient(180deg,rgba(255,255,255,.28),rgba(255,255,255,0) 40%)}
.lt-paper input,.lt-paper textarea,.lt-paper select{width:100%;font:inherit;color:inherit;background:transparent;border:0;outline:0;padding:0}
.lt-paper :focus,.lt-paper :focus-visible{outline:none}
.lt-paper:focus-within{box-shadow:var(--lt-shadow),inset 0 0 0 1px rgba(128,128,128,.32)}
.lt-paper input,.lt-paper select{border-bottom:1px solid transparent;transition:border-color .25s}
.lt-paper input:focus,.lt-paper select:focus{border-bottom-color:rgba(128,128,128,.5)}
.lt-paper input::placeholder,.lt-paper textarea::placeholder{color:inherit;opacity:.45}
.lt-paper select option{color:#2A2230;background:#fff}
.lt-paper .lt-paper__title{font-size:calc(30px * var(--lt-font-scale,1));font-weight:600;line-height:1.2;overflow-wrap:anywhere}
.lt-paper__to{display:flex;flex-wrap:wrap;align-items:center;gap:6px 14px;margin:14px 0 4px;padding-bottom:14px;box-shadow:0 1px 0 rgba(128,128,128,.3);font-size:15px}
.lt-paper__to > *{flex:1 1 180px;min-width:0}
.lt-paper__to select{cursor:pointer}
.lt-paper .lt-paper__label{flex:0 0 auto;width:auto;opacity:.6}
.lt-paper .lt-paper__text{cursor:text;display:block;min-height:34vh;margin-top:14px;resize:none;overflow:hidden;line-height:1.8;font-size:calc(18px * var(--lt-font-scale,1));background-image:linear-gradient(to bottom,transparent calc(1.8em - 1px),rgba(128,128,128,.22) calc(1.8em - 1px));background-size:100% 1.8em}
.lt-read{white-space:pre-wrap;min-height:30vh;margin-top:14px;line-height:1.8;font-size:calc(18px * var(--lt-font-scale,1));overflow-wrap:anywhere;background-image:linear-gradient(to bottom,transparent calc(1.8em - 1px),rgba(128,128,128,.22) calc(1.8em - 1px));background-size:100% 1.8em}
.lt-newline{display:none;position:sticky;bottom:14px;z-index:2;align-items:center;gap:6px;min-height:44px;margin:12px 0 0 auto;padding:0 18px;border-radius:999px;border:1px solid var(--lt-line);background:var(--lt-surface);color:var(--lt-text);font-size:14px;box-shadow:var(--lt-shadow)}
@media (pointer:coarse){.lt-newline{display:inline-flex}}
.lt-controls{display:grid;grid-template-columns:auto 1fr;width:100%;margin:24px 0 22px;border:1px solid var(--lt-line);border-radius:20px;background:var(--lt-surface);box-shadow:0 10px 28px rgba(0,0,0,.08);overflow:hidden;transition:opacity .5s}
@media (hover:hover) and (pointer:fine){.lt-controls.is-quiet{opacity:.45}}
.lt-controls:hover,.lt-controls:focus-within{opacity:1}
.lt-group{display:flex;flex-direction:column;gap:10px;min-width:0;padding:18px 24px 14px}
.lt-group + .lt-group{border-left:1px solid var(--lt-line)}
.lt-group__head{display:flex;align-items:baseline;justify-content:space-between;gap:12px}
.lt-group__label{font-size:11px;letter-spacing:.22em;text-transform:uppercase;color:var(--lt-muted)}
.lt-group__value{font-size:13px;color:var(--lt-text)}
.lt-swatches{display:flex;flex-wrap:wrap;gap:4px;margin-left:-6px}
.lt-swatch{width:44px;height:44px;display:grid;place-items:center;border:0;background:transparent;padding:0}
.lt-swatch span{width:30px;height:30px;border-radius:50%;box-shadow:inset 0 0 0 1px rgba(150,150,150,.55),0 1px 3px rgba(0,0,0,.14);transition:transform .2s,box-shadow .2s}
.lt-swatch:hover span{transform:scale(1.08)}
.lt-swatch[aria-checked="true"] span{box-shadow:inset 0 0 0 1px rgba(150,150,150,.55),0 0 0 3px var(--lt-surface),0 0 0 5px var(--lt-accent)}
.lt-fonts{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}
.lt-fontbtn{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;min-height:64px;padding:8px 10px;border:1px solid var(--lt-line);border-radius:14px;background:transparent;color:var(--lt-muted);transition:border-color .2s,background .2s,color .2s}
.lt-fontbtn:hover{border-color:var(--lt-accent);color:var(--lt-text)}
.lt-fontbtn[aria-checked="true"]{border-color:var(--lt-accent);background:var(--lt-accent-soft);color:var(--lt-text)}
.lt-fontbtn__aa{font-size:26px;line-height:1.1}
.lt-fontbtn small{font:11px var(--lt-ui);letter-spacing:.03em;text-align:center}
@media (max-width:900px){.lt-controls{grid-template-columns:1fr}.lt-group + .lt-group{border-left:0;border-top:1px solid var(--lt-line)}.lt-group{padding:16px 18px 12px}}

/* loading: one 3.6s loop. 0-10% closed, flap opens 12-36%, letter rises 38-54%, rests, slips back 64-74%, flap closes 74-82%, seal 90-97% */
.lt-loading{min-height:62vh;display:grid;place-content:center;justify-items:center;gap:30px;text-align:center}
.lt-loading__label{margin:0;font-size:14px;letter-spacing:.04em;animation:lt-pulse 3.6s ease-in-out infinite}
.lt-stage--load{--env-top:84px;height:240px}
.lt-letter{position:absolute;left:55px;top:calc(var(--env-top) + 14px);z-index:2;width:150px;height:96px;padding:20px 18px;border-radius:6px;background:#F7F1E6;box-shadow:0 4px 12px rgba(0,0,0,.18);animation:lt-ld-letter 3.6s ease-in-out infinite both}
.lt-letter i{display:block;height:2px;margin-bottom:12px;border-radius:2px;background:rgba(70,55,40,.28)}
.lt-letter i:nth-child(1){width:55%}
.lt-letter i:nth-child(2){width:90%}
.lt-letter i:nth-child(3){width:72%}
.lt-loading .lt-flap--shut{transform:scaleY(1);animation:lt-ld-shut 3.6s ease-in-out infinite both}
.lt-loading .lt-flap--open{opacity:0;animation:lt-ld-open 3.6s ease-in-out infinite both}
.lt-loading .lt-seal{animation:lt-ld-seal 3.6s ease-in-out infinite both}
@keyframes lt-ld-shut{0%,10%{transform:scaleY(1)}24%{transform:scaleY(0)}82%{transform:scaleY(0)}92%,100%{transform:scaleY(1)}}
@keyframes lt-ld-open{0%,22%{opacity:0;transform:scaleY(0)}24%{opacity:1;transform:scaleY(0)}36%,74%{opacity:1;transform:scaleY(-1)}82%{opacity:1;transform:scaleY(0)}83%,100%{opacity:0;transform:scaleY(0)}}
@keyframes lt-ld-letter{0%,38%{transform:translateY(0)}54%,64%{transform:translateY(-66px)}74%,100%{transform:translateY(0)}}
@keyframes lt-ld-seal{0%,8%{opacity:1;transform:scale(1)}16%,88%{opacity:0;transform:scale(1)}90%{opacity:0;transform:scale(1.6)}97%,100%{opacity:1;transform:scale(1)}}
@keyframes lt-pulse{0%,100%{opacity:.45}50%{opacity:1}}

/* dialogs */
.lt-overlay{position:absolute;inset:0;z-index:5;display:grid;place-items:center;padding:20px;background:rgba(12,8,16,.62);backdrop-filter:blur(3px);animation:lt-in .25s ease both}
.lt-dialog{width:100%;max-width:440px;max-height:100%;overflow:auto;padding:28px;border-radius:18px;background:var(--lt-surface);border:1px solid var(--lt-line);box-shadow:var(--lt-shadow)}
.lt-dialog h3{margin:0 0 10px;font-family:var(--lt-heading);font-weight:600;font-size:26px;line-height:1.2}
.lt-dialog p{margin:0 0 16px}
.lt-field{display:block;width:100%;min-height:46px;margin-bottom:10px;padding:0 14px;border-radius:12px;border:1px solid var(--lt-line);background:transparent;color:var(--lt-text);font-size:16px}
.lt-field:focus-visible{outline:none;border-color:var(--lt-accent);box-shadow:0 0 0 3px rgba(125,106,153,.28)}
.lt-check{display:flex;gap:12px;align-items:flex-start;margin:4px 0 18px;cursor:pointer}
.lt-check input{width:22px;height:22px;margin-top:2px;accent-color:var(--lt-accent)}
.lt-check small{display:block;color:var(--lt-muted);margin-top:2px}
.lt-dialog__buttons{display:flex;flex-wrap:wrap;gap:10px;justify-content:flex-end;margin-top:6px}
@media (max-width:480px){.lt-dialog__buttons .lt-btn{flex:1 1 100%}}

/* envelope picture: used by the release animation and the Released screen.
   --env-top is where the envelope starts inside its box. */
.lt-stage{--env-top:160px;position:relative;flex:none;width:260px;height:330px}
.lt-stage--still{--env-top:10px;height:170px}
.lt-envpart{position:absolute;left:20px;top:var(--env-top);width:220px;height:140px;border-radius:10px}
.lt-envback{background:var(--lt-env);box-shadow:0 18px 38px rgba(0,0,0,.28)}
.lt-envfront{z-index:3;clip-path:polygon(0 0,50% 54%,100% 0,100% 100%,0 100%);background:var(--lt-env-front)}
.lt-flap{height:78px;clip-path:polygon(0 0,100% 0,50% 100%);transform-origin:50% 0}
.lt-flap--shut{z-index:4;background:var(--lt-env-flap);filter:drop-shadow(0 2px 2px rgba(60,40,20,.18))}
.lt-flap--open{z-index:1;background:var(--lt-env-open)}
.lt-seal{position:absolute;left:130px;top:calc(var(--env-top) + 76px);width:40px;height:40px;margin:-20px 0 0 -20px;z-index:5;display:grid;place-items:center;border-radius:50%;color:#F5F0F2;font-size:17px;background:radial-gradient(circle at 35% 30%,#CDBFDC,#9886B3);box-shadow:inset 0 1px 2px rgba(255,255,255,.45),inset 0 -2px 3px rgba(60,40,90,.35),0 2px 4px rgba(40,25,50,.3);text-shadow:0 1px 1px rgba(60,40,90,.5)}
.lt-sheet{position:absolute;left:50px;top:10px;width:160px;height:210px;z-index:2}
.lt-panel{position:absolute;left:0;width:160px;height:70px;background:#F7F1E6;overflow:hidden;box-shadow:0 5px 14px rgba(0,0,0,.18)}
.lt-panel i{position:absolute;left:14px;height:2px;border-radius:2px;background:rgba(70,55,40,.22)}
.lt-panel--top{top:0;border-radius:8px 8px 0 0;transform-origin:50% 100%;z-index:3}
.lt-panel--mid{top:70px;z-index:1}
.lt-panel--bot{top:140px;border-radius:0 0 8px 8px;transform-origin:50% 0;z-index:2}

/* release animation: 2800ms in four quiet steps (fold, envelope, seal, fade) */
.lt-flight{position:absolute;inset:0;z-index:3;display:grid;place-items:center;pointer-events:none}
.lt-flight .lt-stage{animation:lt-fly 800ms cubic-bezier(.45,0,.2,1) 2000ms both}
.lt-flight .lt-sheet{animation:lt-rise 400ms ease-out both,lt-slide 700ms cubic-bezier(.65,0,.35,1) 800ms both}
.lt-flight .lt-panel--bot{animation:lt-fold-bot 450ms ease-in-out 150ms both}
.lt-flight .lt-panel--top{animation:lt-fold-top 400ms ease-in-out 450ms both}
.lt-flight .lt-envback,.lt-flight .lt-envfront{opacity:0;animation:lt-env 450ms ease-out 500ms both}
.lt-flight .lt-flap--open{opacity:0;animation:lt-flap-open 1100ms linear 500ms both}
.lt-flight .lt-flap--shut{opacity:0;transform:scaleY(0);animation:lt-flap-shut 350ms ease-out 1500ms both}
.lt-flight .lt-seal{opacity:0;animation:lt-stamp 350ms ease-out 1650ms both}
@keyframes lt-rise{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
@keyframes lt-fold-top{to{transform:perspective(800px) rotateX(-180deg);background:#E8E1D2}}
@keyframes lt-fold-bot{to{transform:perspective(800px) rotateX(180deg);background:#EDE6D8}}
@keyframes lt-slide{to{transform:translateY(118px) scale(.92)}}
@keyframes lt-env{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:none}}
@keyframes lt-flap-open{0%{opacity:0;transform:translateY(22px) scaleY(-1)}40%{opacity:1;transform:scaleY(-1)}72%{opacity:1;transform:scaleY(-1)}100%{opacity:1;transform:scaleY(0)}}
@keyframes lt-flap-shut{from{opacity:1;transform:scaleY(0)}to{opacity:1;transform:scaleY(1)}}
@keyframes lt-stamp{from{opacity:0;transform:scale(1.7)}to{opacity:1;transform:scale(1)}}
/* last step: the sealed envelope travels to the top of the page and stays there */
@keyframes lt-fly{to{transform:translate(var(--dx,0px),var(--dy,0px))}}
.lt-stage--still .lt-flap--shut,.lt-stage--still .lt-envfront,.lt-stage--still .lt-envback{opacity:1}

/* Released screen */
.lt-done{position:relative;flex:1;display:grid;place-items:center;padding:24px 20px 56px;overflow-y:auto}
.lt-done__inner{width:100%;max-width:760px;text-align:center}
.lt-done[data-flying="true"] .lt-still,.lt-done[data-flying="true"] .lt-after{opacity:0}
.lt-done[data-flying="false"] .lt-after{animation:lt-in .9s ease both}
.lt-done__inner .lt-stage{margin:0 auto 18px}
.lt-done .lt-muted{max-width:440px;margin:0 auto 6px}
.lt-done__note{font-size:13px;color:var(--lt-muted);margin:10px 0 26px}

@media (prefers-reduced-motion:reduce){.lt *{animation-duration:.01ms!important;animation-delay:0ms!important;animation-iteration-count:1!important;transition:none!important}}
`;

/* ═════════════════════════════════════════════════════════════════════════
   6. BUILDING BLOCKS
   ═════════════════════════════════════════════════════════════════════════ */

/** A centred box over the page. Closes with Esc or a click outside. */
const Dialog: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({
  title,
  onClose,
  children,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="lt-overlay" onMouseDown={onClose}>
      <div className="lt-dialog" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.stopPropagation()}>
        <h3>{title}</h3>
        {children}
      </div>
    </div>
  );
};

/** The sealed envelope with the Astria seal. `children` is placed inside the box (the release animation adds the paper). */
const Envelope: React.FC<{ still?: boolean; extraClass?: string; children?: React.ReactNode }> = ({
  still,
  extraClass = "",
  children,
}) => (
  <div className={`lt-stage ${still ? "lt-stage--still" : ""} ${extraClass}`} aria-hidden="true">
    <div className="lt-envpart lt-envback" />
    {!still && <div className="lt-envpart lt-flap lt-flap--open" />}
    {children}
    <div className="lt-envpart lt-envfront" />
    <div className="lt-envpart lt-flap lt-flap--shut" style={still ? { transform: "scaleY(1)" } : undefined} />
    <div className="lt-seal">✦</div>
  </div>
);

/**
 * Shown while the letters are being opened. The same ivory envelope as the release animation, played
 * the other way round: the seal lifts, the flap opens, a letter rises out and slips back in, and it closes again.
 */
const LoadingLetters: React.FC<{ label: string }> = ({ label }) => (
  <div className="lt-loading" role="status" aria-live="polite">
    <Envelope extraClass="lt-stage--load">
      <div className="lt-letter">
        <i />
        <i />
        <i />
      </div>
    </Envelope>
    <p className="lt-muted lt-loading__label">{label}</p>
  </div>
);

/** A text box with the same look everywhere. */
const PassphraseInput: React.FC<{
  value: string;
  onChange: (v: string) => void;
  label: string;
  autoFocus?: boolean;
  onEnter?: () => void;
}> = ({ value, onChange, label, autoFocus, onEnter }) => (
  <input
    className="lt-field"
    type="password"
    aria-label={label}
    placeholder={label}
    autoFocus={autoFocus}
    autoComplete="off"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onKeyDown={(e) => e.key === "Enter" && onEnter?.()}
  />
);

/** Asks for a new passphrase (twice) and seals the letter. */
const SealDialog: React.FC<{ copy: Copy; onClose: () => void; onSeal: (passphrase: string) => Promise<void> }> = ({
  copy,
  onClose,
  onSeal,
}) => {
  const [pass, setPass] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (pass !== again) return setError(copy("passphraseMismatch"));
    setBusy(true);
    setError("");
    try {
      await onSeal(pass);
    } catch (err) {
      setError(explain(err, copy));
      setBusy(false);
    }
  };

  return (
    <Dialog title={copy("sealTitle")} onClose={onClose}>
      <p className="lt-muted">{copy("sealText")}</p>
      <PassphraseInput value={pass} onChange={setPass} label={copy("passphrase")} autoFocus />
      <PassphraseInput value={again} onChange={setAgain} label={copy("passphraseAgain")} onEnter={submit} />
      {error && <p className="lt-error">{error}</p>}
      <div className="lt-dialog__buttons">
        <button type="button" className="lt-btn lt-btn--text" onClick={onClose}>
          {copy("close")}
        </button>
        <button type="button" className="lt-btn lt-btn--primary" disabled={busy || !pass || !again} onClick={submit}>
          {copy("sealNow")}
        </button>
      </div>
    </Dialog>
  );
};

/** Asks for the passphrase of a sealed letter. onUnlock throws when it is wrong. */
const UnlockDialog: React.FC<{ copy: Copy; onClose: () => void; onUnlock: (passphrase: string) => Promise<void> }> = ({
  copy,
  onClose,
  onUnlock,
}) => {
  const [pass, setPass] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await onUnlock(pass);
    } catch (err) {
      setError(explain(err, copy));
      setBusy(false);
    }
  };

  return (
    <Dialog title={copy("unlockTitle")} onClose={onClose}>
      <p className="lt-muted">{copy("unlockText")}</p>
      <PassphraseInput value={pass} onChange={setPass} label={copy("passphrase")} autoFocus onEnter={submit} />
      {error && <p className="lt-error">{error}</p>}
      <div className="lt-dialog__buttons">
        <button type="button" className="lt-btn lt-btn--text" onClick={onClose}>
          {copy("close")}
        </button>
        <button type="button" className="lt-btn lt-btn--primary" disabled={busy || !pass} onClick={submit}>
          {copy("unlock")}
        </button>
      </div>
    </Dialog>
  );
};

/** "Are you sure?" for deleting a letter or erasing a retained copy. */
const ConfirmDialog: React.FC<{
  title: string;
  text: string;
  confirmLabel: string;
  cancelLabel: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  copy: Copy;
}> = ({ title, text, confirmLabel, cancelLabel, onClose, onConfirm, copy }) => {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } catch (err) {
      setError(explain(err, copy));
      setBusy(false);
    }
  };
  return (
    <Dialog title={title} onClose={onClose}>
      <p className="lt-muted">{text}</p>
      {error && <p className="lt-error">{error}</p>}
      <div className="lt-dialog__buttons">
        <button type="button" className="lt-btn" onClick={onClose}>
          {cancelLabel}
        </button>
        <button type="button" className="lt-btn lt-btn--danger" disabled={busy} onClick={submit}>
          {confirmLabel}
        </button>
      </div>
    </Dialog>
  );
};

/**
 * "Release this letter?". The letter is released on the server first; only after that
 * succeeds does the screen move on to the animation. If it fails nothing has changed.
 */
const ReleaseDialog: React.FC<{
  copy: Copy;
  letter: { id: string; status: Status };
  passphrase: string | null; // already known when the letter was unlocked this visit
  onClose: () => void;
  onReleased: (letter: Letter) => void;
}> = ({ copy, letter, passphrase, onClose, onReleased }) => {
  const [keep, setKeep] = useState(true);
  const [typed, setTyped] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // A sealed letter needs its passphrase only when a private copy is being kept.
  const askPassphrase = letter.status === "sealed" && keep && !passphrase;

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const released = await releaseLetter(letter.id, { retainCopy: keep, passphrase: passphrase || typed || undefined });
      onReleased(released);
    } catch (err) {
      const message = explain(err, copy);
      setError(message === copy("errGeneric") || message === copy("errNetwork") ? copy("releaseFailed") : message);
      setBusy(false);
    }
  };

  return (
    <Dialog title={copy("releaseTitle")} onClose={onClose}>
      <p className="lt-muted">{copy("releaseText")}</p>
      <label className="lt-check">
        <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} />
        <span>
          {copy("keepCopy")}
          <small>{copy("keepCopyHint")}</small>
        </span>
      </label>
      {askPassphrase && <PassphraseInput value={typed} onChange={setTyped} label={copy("passphrase")} autoFocus onEnter={submit} />}
      {error && <p className="lt-error">{error}</p>}
      <div className="lt-dialog__buttons">
        <button type="button" className="lt-btn lt-btn--text" onClick={onClose}>
          {copy("notYet")}
        </button>
        <button type="button" className="lt-btn lt-btn--primary" disabled={busy || (askPassphrase && !typed)} onClick={submit}>
          {copy("releaseLetter")}
        </button>
      </div>
    </Dialog>
  );
};

/* ═════════════════════════════════════════════════════════════════════════
   7. SCREENS
   ═════════════════════════════════════════════════════════════════════════ */

const useDate = () => {
  const { i18n } = useTranslation();
  return (iso: string | null) =>
    iso ? new Date(iso).toLocaleDateString(i18n.language || undefined, { year: "numeric", month: "short", day: "numeric" }) : "";
};

/* ── 7a. My Letters ─────────────────────────────────────────────────────── */

const LetterCard: React.FC<{
  letter: Letter;
  copy: Copy;
  onOpen: (l: Letter) => void;
  onDelete: (l: Letter) => void;
}> = ({ letter, copy, onOpen, onDelete }) => {
  const formatDate = useDate();
  const to = letter.recipientType === "anonymous" ? recipientOf("anonymous").name : letter.recipientName || recipientOf(letter.recipientType).name;
  const title = letter.title || copy("untitled");

  // What the small grey line under the title says depends on the letter's life stage.
  const when =
    letter.status === "sealed"
      ? copy("sealedOn", { date: formatDate(letter.sealedAt || letter.updatedAt) })
      : letter.status === "released"
        ? copy("releasedOn", { date: formatDate(letter.releasedAt || letter.updatedAt) })
        : formatDate(letter.updatedAt);

  return (
    <li className="lt-card" style={paperStyle(letter.paper, letter.font)}>
      <button type="button" className="lt-card__main" onClick={() => onOpen(letter)}>
        <span className="lt-card__title">{title}</span>
        <span className="lt-card__meta">
          {copy("to", { name: to })} · {when}
        </span>

        {letter.status === "sealed" ? (
          <span className="lt-card__sealed">
            <Lock size={16} aria-hidden="true" />
            {letter.oldId ? copy("needsUnlock") : copy("protectedBy")}
          </span>
        ) : letter.preview ? (
          <span className="lt-card__body">{letter.preview}</span>
        ) : letter.status === "released" ? (
          <span className="lt-card__body">{copy("noCopy")}</span>
        ) : null}

        {letter.status === "draft" && <span className="lt-card__foot">{copy("stillInProgress")}</span>}
      </button>
      {letter.status !== "released" && !letter.oldId && (
        <button type="button" className="lt-card__delete" aria-label={copy("deleteLetter")} onClick={() => onDelete(letter)}>
          <Trash2 size={17} aria-hidden="true" />
        </button>
      )}
    </li>
  );
};

const MyLetters: React.FC<{
  letters: Letter[];
  limit: Limit | null;
  notice: string;
  copy: Copy;
  onNew: () => void;
  onOpen: (l: Letter) => void;
  onDelete: (l: Letter) => void;
}> = ({ letters, limit, notice, copy, onNew, onOpen, onDelete }) => {
  // The four groups, in the order they are shown. A group with no letters is not shown at all.
  const groups: { status: Status; title: TextKey; hint: TextKey; icon: React.ReactNode }[] = [
    { status: "draft", title: "draftsTitle", hint: "draftsHint", icon: <PenLine size={19} aria-hidden="true" /> },
    { status: "sealed", title: "sealedTitle", hint: "sealedHint", icon: <Lock size={19} aria-hidden="true" /> },
    { status: "kept", title: "keptTitle", hint: "keptHint", icon: <Lock size={19} aria-hidden="true" /> },
    { status: "released", title: "releasedTitle", hint: "releasedHint", icon: <Sparkles size={19} aria-hidden="true" /> },
  ];

  if (letters.length === 0) {
    return (
      <div className="lt-wide lt-view">
        {notice && <p className="lt-notice">{notice}</p>}
        <div className="lt-empty">
          <p className="lt-eyebrow">{copy("eyebrow")}</p>
          <h1 className="lt-h1">{copy("emptyTitle")}</h1>
          <p className="lt-muted">{copy("emptyText")}</p>
          <div className="lt-actions" style={{ marginTop: 28 }}>
            <button type="button" className="lt-btn lt-btn--primary" onClick={onNew}>
              {copy("writeLetter")}
            </button>
          </div>
          <p className="lt-muted" style={{ fontSize: 13, marginTop: 18 }}>
            {copy("private")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="lt-wide lt-view">
      <div className="lt-hero">
        <p className="lt-eyebrow">{copy("eyebrow")}</p>
        <h1 className="lt-h1">{copy("subtitle")}</h1>
        <p className="lt-muted">
          {copy("steps")}
          <br />
          {copy("private")}
        </p>
        <div className="lt-actions" style={{ marginTop: 22 }}>
          <button type="button" className="lt-btn lt-btn--primary" onClick={onNew}>
            {copy("newLetter")}
          </button>
        </div>
        {limit && limit.max !== null && (
          <p className="lt-muted" style={{ fontSize: 13, marginTop: 12 }}>
            {copy("planUsage", { used: Math.min(limit.used, limit.max), max: limit.max })}
          </p>
        )}
        {notice && <p className="lt-notice">{notice}</p>}
      </div>

      {groups.map((group) => {
        const items = letters.filter((l) => l.status === group.status);
        if (items.length === 0) return null;
        return (
          <section className="lt-section" key={group.status}>
            <div className="lt-section__head">
              <h2 className="lt-section__title">
                {group.icon}
                {copy(group.title)}
              </h2>
              <span className="lt-section__hint">{copy(group.hint)}</span>
            </div>
            <ul className="lt-grid" style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {items.map((letter) => (
                <LetterCard key={letter.id} letter={letter} copy={copy} onOpen={onOpen} onDelete={onDelete} />
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

/* ── 7b. Studio: where a letter is written ──────────────────────────────── */

interface StudioStart {
  letter: Letter | null; // null = a brand new letter
  passphrase: string | null; // set when reopening a sealed letter for editing
}

const Studio: React.FC<{
  start: StudioStart;
  copy: Copy;
  onBack: () => void;
  onSealed: () => void;
  onReleased: (letter: Letter) => void;
  onLimit: () => void;
}> = ({ start, copy, onBack, onSealed, onReleased, onLimit }) => {
  const existing = start.letter;
  const editingSealed = !!start.passphrase;

  // What is on the page right now.
  const [form, setForm] = useState({
    title: existing?.title && existing.title !== "Untitled Letter" ? existing.title : "",
    recipientType: existing?.recipientType || "other",
    recipientName: existing?.recipientName || "",
    paper: existing?.paper || "soft_ivory",
    font: existing?.font || "classic_serif",
    content: existing?.content || "",
  });
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [quiet, setQuiet] = useState(false); // controls fade while typing
  const [dialog, setDialog] = useState<null | "seal" | "release">(null);

  // ── Autosave ──
  // Every change marks the page "dirty" and starts a short timer. When it fires (or when
  // the person leaves), the latest text is sent to the server. Saves run one at a time,
  // so a new letter can never be created twice.
  const letterId = useRef<string | null>(existing?.id || null);
  const latest = useRef(form);
  latest.current = form;
  const dirty = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  const queue = useRef<Promise<boolean>>(Promise.resolve(true));

  const onLimitRef = useRef(onLimit);
  onLimitRef.current = onLimit;

  const saveNow = useCallback(async (): Promise<boolean> => {
    if (!dirty.current) return true;
    const page = latest.current;
    const nothingWritten = !page.title.trim() && !page.content.trim();
    if (!letterId.current && nothingWritten) return true; // do not create empty letters

    dirty.current = false;
    setSaveState("saving");
    try {
      const fields = { ...page, title: page.title.trim() || "Untitled Letter" };
      if (!letterId.current) {
        letterId.current = (await createLetter(fields)).id;
      } else {
        await saveLetter(letterId.current, { ...fields, passphrase: start.passphrase || undefined });
      }
      setSaveState(dirty.current ? "saving" : "saved");
      return true;
    } catch (err) {
      dirty.current = true;
      setSaveState("error");
      if (isLimitError(err)) onLimitRef.current();
      return false;
    }
  }, [start.passphrase]);

  const flush = useCallback(() => {
    window.clearTimeout(timer.current);
    queue.current = queue.current.then(saveNow);
    return queue.current;
  }, [saveNow]);

  const change = (patch: Partial<typeof form>) => {
    setForm((f) => ({ ...f, ...patch }));
    dirty.current = true;
    setSaveState("idle");
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(flush, 700);
  };

  // Save what is left when the screen closes, and warn before the tab closes with unsaved words.
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (dirty.current) e.preventDefault();
    };
    window.addEventListener("beforeunload", warn);
    return () => {
      window.removeEventListener("beforeunload", warn);
      flush();
    };
  }, [flush]);

  // The writing box grows with the letter. While growing it also counts how many lines
  // the words use, so a tap on an empty ruled line below them can be turned into that line.
  const box = useRef<HTMLTextAreaElement>(null);
  const usedLines = useRef(1);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    el.style.minHeight = "0";
    el.style.height = "auto";
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 33;
    usedLines.current = Math.max(1, Math.round(el.scrollHeight / lineHeight));
    el.style.minHeight = "";
    el.style.height = `${el.scrollHeight + lineHeight * 2}px`; // two spare lines below the words
  }, [form.content, form.font]);

  // Tapping an empty ruled line below the words moves the cursor to that line (by adding
  // line breaks up to it). Enter and Shift+Enter are left alone: they still start a new line.
  // For phone keyboards without a usable Enter key: starts a new line at the cursor.
  const insertNewLine = () => {
    const el = box.current;
    if (!el) return;
    const at = document.activeElement === el ? [el.selectionStart, el.selectionEnd] : [form.content.length, form.content.length];
    change({ content: `${form.content.slice(0, at[0])}\n${form.content.slice(at[1])}` });
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(at[0] + 1, at[0] + 1);
    });
  };

  const goToTappedLine = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 33;
    const tappedLine = Math.floor((e.clientY - el.getBoundingClientRect().top) / lineHeight);
    const missing = tappedLine - (usedLines.current - 1);
    if (missing <= 0) return; // a line that already has words: the browser places the cursor itself
    const next = form.content + "\n".repeat(Math.min(missing, 50));
    change({ content: next });
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(next.length, next.length);
    });
  };

  // ── Actions ──
  const leave = async () => {
    if (await flush()) onBack();
  };
  const openDialog = async (which: "seal" | "release") => {
    if ((await flush()) && letterId.current) setDialog(which);
  };
  const hasWords = form.content.trim().length > 0;

  const sealIt = async (passphrase: string) => {
    await sealLetter(letterId.current as string, passphrase);
    onSealed();
  };

  return (
    <div className="lt-narrow lt-view" onPointerMove={() => quiet && setQuiet(false)}>
      <div className="lt-bar">
        <button type="button" className="lt-btn lt-btn--text" onClick={leave} style={{ paddingLeft: 8 }}>
          <ArrowLeft size={16} aria-hidden="true" />
          {copy("myLetters")}
        </button>
        <span className="lt-status" role="status" aria-live="polite">
          {saveState === "saving" && copy("saving")}
          {saveState === "saved" && copy("saved")}
          {saveState === "error" && copy("saveFailed")}
        </span>
      </div>

      {/* The page. It already shows the chosen paper and handwriting. */}
      <div className="lt-paper" style={paperStyle(form.paper, form.font)}>
        <input
          className="lt-paper__title"
          value={form.title}
          maxLength={120}
          placeholder={copy("titlePlaceholder")}
          aria-label={copy("titlePlaceholder")}
          onChange={(e) => change({ title: e.target.value })}
        />
        <div className="lt-paper__to">
          <span className="lt-paper__label">{copy("toLabel")}</span>
          <select
            value={form.recipientType}
            aria-label={copy("whoFor")}
            onChange={(e) => change({ recipientType: e.target.value })}
          >
            {RECIPIENTS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          {form.recipientType !== "anonymous" && (
            <input
              value={form.recipientName}
              maxLength={80}
              placeholder={copy("namePlaceholder")}
              aria-label={copy("namePlaceholder")}
              onChange={(e) => change({ recipientName: e.target.value })}
            />
          )}
        </div>
        <textarea
          ref={box}
          className="lt-paper__text"
          value={form.content}
          maxLength={20000}
          placeholder={copy("bodyPlaceholder")}
          aria-label={copy("bodyPlaceholder")}
          autoFocus={!existing}
          enterKeyHint="enter"
          onClick={goToTappedLine}
          onChange={(e) => {
            setQuiet(true);
            change({ content: e.target.value });
          }}
        />
      </div>

      {/* Touch screens only: a button that is always within reach to go to the next line. */}
      <button type="button" className="lt-newline" onPointerDown={(e) => e.preventDefault()} onClick={insertNewLine}>
        ↵ {copy("newLine")}
      </button>

      {/* Paper and handwriting choices. They fade while writing and return when the pointer moves. */}
      <div className={`lt-controls ${quiet ? "is-quiet" : ""}`}>
        <div className="lt-group">
          <div className="lt-group__head">
            <span className="lt-group__label">{copy("paper")}</span>
            <span className="lt-group__value">{paperOf(form.paper).name}</span>
          </div>
          <div className="lt-swatches" role="radiogroup" aria-label={copy("paper")}>
            {PAPERS.map((p) => (
              <button
                key={p.id}
                type="button"
                role="radio"
                aria-checked={form.paper === p.id}
                aria-label={p.name}
                title={p.name}
                className="lt-swatch"
                onClick={() => change({ paper: p.id })}
              >
                <span style={{ background: p.paper }} />
              </button>
            ))}
          </div>
        </div>
        <div className="lt-group">
          <div className="lt-group__head">
            <span className="lt-group__label">{copy("font")}</span>
          </div>
          <div className="lt-fonts" role="radiogroup" aria-label={copy("font")}>
            {FONTS.map((f) => (
              <button
                key={f.id}
                type="button"
                role="radio"
                aria-checked={form.font === f.id}
                className="lt-fontbtn"
                onClick={() => change({ font: f.id })}
              >
                <span className="lt-fontbtn__aa" style={{ fontFamily: f.family }}>
                  Aa
                </span>
                <small>{f.name}</small>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="lt-actions">
        {editingSealed ? (
          <button type="button" className="lt-btn" onClick={leave}>
            {copy("saveSealed")}
          </button>
        ) : (
          <>
            <button type="button" className="lt-btn" onClick={leave}>
              {copy("saveForLater")}
            </button>
            <button type="button" className="lt-btn" disabled={!hasWords} onClick={() => openDialog("seal")}>
              {copy("sealAndKeep")}
            </button>
          </>
        )}
        <button type="button" className="lt-btn lt-btn--primary" disabled={!hasWords} onClick={() => openDialog("release")}>
          {copy("releaseLetter")}
        </button>
      </div>

      {dialog === "seal" && <SealDialog copy={copy} onClose={() => setDialog(null)} onSeal={sealIt} />}
      {dialog === "release" && letterId.current && (
        <ReleaseDialog
          copy={copy}
          letter={{ id: letterId.current, status: editingSealed ? "sealed" : "draft" }}
          passphrase={start.passphrase}
          onClose={() => setDialog(null)}
          onReleased={onReleased}
        />
      )}
    </div>
  );
};

/* ── 7c. Reader: a letter that can be read but not typed into ───────────── */

const Reader: React.FC<{
  letter: Letter; // has `content` filled in
  passphrase: string | null;
  copy: Copy;
  onBack: () => void;
  onEdit: () => void;
  onReleased: (letter: Letter) => void;
  onCopyErased: () => void;
}> = ({ letter, passphrase, copy, onBack, onEdit, onReleased, onCopyErased }) => {
  const formatDate = useDate();
  const [dialog, setDialog] = useState<null | "release" | "erase">(null);
  const to = letter.recipientType === "anonymous" ? recipientOf("anonymous").name : letter.recipientName || recipientOf(letter.recipientType).name;
  const released = letter.status === "released";

  return (
    <div className="lt-narrow lt-view">
      <div className="lt-bar">
        <button type="button" className="lt-btn lt-btn--text" onClick={onBack} style={{ paddingLeft: 8 }}>
          <ArrowLeft size={16} aria-hidden="true" />
          {copy("myLetters")}
        </button>
        <span className="lt-status">{released ? copy("releasedOn", { date: formatDate(letter.releasedAt) }) : copy("protectedBy")}</span>
      </div>

      <article className="lt-paper" style={paperStyle(letter.paper, letter.font)}>
        <h2 className="lt-paper__title" style={{ margin: 0 }}>
          {letter.title || copy("untitled")}
        </h2>
        <div className="lt-paper__to">
          <span>{copy("to", { name: to })}</span>
        </div>
        <div className="lt-read">{letter.content || (released ? copy("noCopyText") : "")}</div>
      </article>

      <div className="lt-actions" style={{ marginTop: 26 }}>
        {released ? (
          letter.retainedCopy && (
            <button type="button" className="lt-btn lt-btn--danger" onClick={() => setDialog("erase")}>
              {copy("eraseCopy")}
            </button>
          )
        ) : (
          <>
            {letter.status === "sealed" && (
              <button type="button" className="lt-btn" onClick={onEdit}>
                {copy("editLetter")}
              </button>
            )}
            <button type="button" className="lt-btn lt-btn--primary" onClick={() => setDialog("release")}>
              {copy("releaseLetter")}
            </button>
          </>
        )}
      </div>

      {dialog === "release" && (
        <ReleaseDialog
          copy={copy}
          letter={{ id: letter.id, status: letter.status }}
          passphrase={passphrase}
          onClose={() => setDialog(null)}
          onReleased={onReleased}
        />
      )}
      {dialog === "erase" && (
        <ConfirmDialog
          copy={copy}
          title={copy("eraseTitle")}
          text={copy("eraseText")}
          confirmLabel={copy("eraseNow")}
          cancelLabel={copy("keepIt")}
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await eraseCopy(letter.id);
            onCopyErased();
          }}
        />
      )}
    </div>
  );
};

/* ── 7d. Released: the quiet screen after letting go ────────────────────── */

/**
 * The release animation and the Released screen are one screen, so the envelope never leaves it.
 * With `play`, the envelope is folded, sealed and then flies up to the top of the page, landing
 * exactly on the still envelope underneath (which was waiting there, hidden). Then the words fade in.
 */
const ReleasedScreen: React.FC<{ copy: Copy; play: boolean; onJourney: () => void; onMyLetters: () => void }> = ({
  copy,
  play,
  onJourney,
  onMyLetters,
}) => {
  const [flying, setFlying] = useState(play);
  const screen = useRef<HTMLDivElement>(null);

  // Works out how far the flying envelope has to travel to land on the still one.
  const aim = useCallback(() => {
    const from = screen.current?.querySelector<HTMLElement>(".lt-flight .lt-stage");
    const to = screen.current?.querySelector<HTMLElement>(".lt-done__inner .lt-stage");
    if (!from || !to) return;
    const a = from.getBoundingClientRect();
    const b = to.getBoundingClientRect();
    // the envelope sits 160px down inside the flying box and 10px down inside the still one
    from.style.setProperty("--dx", `${b.left - a.left}px`);
    from.style.setProperty("--dy", `${b.top + 10 - (a.top + 160)}px`);
  }, []);
  useLayoutEffect(() => {
    if (flying) aim();
  }, [flying, aim]);

  // The animation reports when it lands; this timer is only a safety net.
  useEffect(() => {
    if (!flying) return;
    const timer = window.setTimeout(() => setFlying(false), 3800);
    return () => window.clearTimeout(timer);
  }, [flying]);

  const lines = (tops: number[], widths: number[]) =>
    tops.map((top, i) => <i key={top} style={{ top, width: widths[i] }} />);

  return (
    <div className="lt-done" data-flying={flying ? "true" : "false"} ref={screen}>
      {flying && (
        <div
          className="lt-flight"
          onAnimationStart={(e) => e.animationName === "lt-fly" && aim()}
          onAnimationEnd={(e) => e.animationName === "lt-fly" && setFlying(false)}
        >
          <Envelope>
            <div className="lt-sheet">
              <div className="lt-panel lt-panel--top">{lines([16, 32, 48], [60, 120, 110])}</div>
              <div className="lt-panel lt-panel--mid">{lines([10, 26, 42, 58], [118, 124, 96, 120])}</div>
              <div className="lt-panel lt-panel--bot">{lines([10, 26, 42], [122, 100, 70])}</div>
            </div>
          </Envelope>
        </div>
      )}
      <div className="lt-done__inner">
        <div className="lt-still">
          <Envelope still />
        </div>
        <div className="lt-after">
          <p className="lt-eyebrow">{copy("releasedEyebrow")}</p>
          <h1 className="lt-h1">{copy("releasedHeading")}</h1>
          <p className="lt-muted">{copy("releasedMessage")}</p>
          <p className="lt-done__note">{copy("releasedNote")}</p>
          <div className="lt-actions">
            <button type="button" className="lt-btn lt-btn--primary" onClick={onJourney}>
              {copy("returnToJourney")}
            </button>
            <button type="button" className="lt-btn" onClick={onMyLetters}>
              {copy("myLetters")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═════════════════════════════════════════════════════════════════════════
   8. LETTERMODE: decides which screen is showing
   ═════════════════════════════════════════════════════════════════════════ */

// Exactly one of these is true at a time.
type View =
  | { name: "list" }
  | { name: "studio"; start: StudioStart }
  | { name: "reader"; letter: Letter; passphrase: string | null }
  | { name: "released"; play: boolean }; // play = show the release animation first

// A box over the list: opening a sealed letter, deleting, or the Free-plan limit.
type ListDialog =
  | null
  | { kind: "unlock"; letter: Letter }
  | { kind: "delete"; letter: Letter }
  | { kind: "limit" };

const LetterMode: React.FC<{ onExit: () => void }> = ({ onExit }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const copy: Copy = (key, values) => t(`astria.lt_${key}`, { defaultValue: TEXT[key], ...values });

  const [view, setView] = useState<View>({ name: "list" });
  const [dialog, setDialog] = useState<ListDialog>(null);
  const [letters, setLetters] = useState<Letter[]>([]);
  const [oldSealed, setOldSealed] = useState<OldLetter[]>([]);
  const [limit, setLimit] = useState<Limit | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [notice, setNotice] = useState("");

  // Fonts for the three handwriting styles are loaded once, when this feature first opens.
  useEffect(() => {
    if (document.getElementById(FONT_LINK_ID)) return;
    const link = document.createElement("link");
    link.id = FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = FONT_LINK_HREF;
    document.head.appendChild(link);
  }, []);

  const showNotice = useCallback((text: string) => {
    setNotice(text);
    window.setTimeout(() => setNotice(""), 5000);
  }, []);

  // Loads the list from the server. First, any letters from the old version are moved over.
  const load = useCallback(async (firstTime = false) => {
    try {
      let moved = 0;
      if (firstTime) {
        try {
          moved = await moveOldLettersToAccount();
        } catch {
          /* they stay in the browser and are tried again next time */
        }
      }
      const data = await listLetters();
      setLetters(data.letters);
      setLimit(data.limit);
      setOldSealed(readOldLetters().filter((l) => l.status === "sealed"));
      setLoadFailed(false);
      if (moved > 0) showNotice(TEXT.oldMoved);
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [showNotice]);
  useEffect(() => {
    load(true);
  }, [load]);

  const goToList = useCallback(async () => {
    setView({ name: "list" });
    await load();
  }, [load]);

  // ── What the person can do from the list ──
  const startNewLetter = () => {
    if (limit && limit.max !== null && limit.used >= limit.max) return setDialog({ kind: "limit" });
    setView({ name: "studio", start: { letter: null, passphrase: null } });
  };

  const openFromList = async (letter: Letter) => {
    if (letter.status === "sealed") return setDialog({ kind: "unlock", letter });
    try {
      const full = await openLetter(letter.id);
      setView(letter.status === "draft" ? { name: "studio", start: { letter: full, passphrase: null } } : { name: "reader", letter: full, passphrase: null });
    } catch {
      showNotice(copy("errGeneric"));
    }
  };

  // Called by the unlock box. Throws (so the box shows the error) when the passphrase is wrong.
  const unlock = async (letter: Letter, passphrase: string) => {
    if (letter.oldId) {
      // An old browser-encrypted letter: open it here, then move it into the account.
      const old = oldSealed.find((o) => o.id === letter.oldId) as OldLetter;
      let text: string;
      try {
        text = await decryptOldLetter(old, passphrase);
      } catch {
        // Same shape as a server reply, so the unlock box shows its normal "wrong passphrase" message.
        throw Object.assign(new Error("wrong passphrase"), { response: { data: { code: "WRONG_PASSPHRASE" } } });
      }
      const { data } = await api.post<{ created: { legacyId: string; id: string }[] }>(`${BASE}/import`, {
        letters: [oldToImport(old, { text, passphrase })],
      });
      removeOldLetters([old.id]);
      const id = data.created[0]?.id;
      setDialog(null);
      if (!id) return goToList();
      const full = await unlockLetter(id, passphrase);
      setView({ name: "reader", letter: full, passphrase });
      return;
    }
    const full = await unlockLetter(letter.id, passphrase);
    setDialog(null);
    setView({ name: "reader", letter: full, passphrase });
  };

  const afterRelease = () => {
    // Released on the server already. Play the animation, then show the quiet screen.
    setDialog(null);
    setView({ name: "released", play: !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches });
  };

  const leaveFeature = () => {
    onExit();
    navigate("/journey");
  };

  // ── Which screen ──
  const allLetters = [...letters, ...oldSealed.map(oldSealedAsLetter)];
  const title = view.name === "list" ? copy("myLetters") : view.name === "released" ? "" : copy("title");

  return (
    <div className="lt" aria-label={copy("title")}>
      <style>{STYLES}</style>

      <header className="lt-top">
        {/* The round back arrow (leave the feature) is only on My Letters. Other screens
            have their own "← My Letters" button. */}
        {view.name === "list" ? (
          <button type="button" className="lt-iconbtn" aria-label={copy("back")} onClick={onExit}>
            <ArrowLeft size={20} aria-hidden="true" />
          </button>
        ) : (
          <span />
        )}
        <h2 className="lt-top__title">{title}</h2>
        <span />
      </header>

      {view.name === "released" && <ReleasedScreen copy={copy} play={view.play} onJourney={leaveFeature} onMyLetters={goToList} />}

      {view.name === "list" && (
        <div className="lt-scroll">
          {loading ? (
            <LoadingLetters label={copy("loading")} />
          ) : loadFailed ? (
            <div className="lt-narrow" style={{ textAlign: "center", paddingTop: 60 }}>
              <p className="lt-muted">{copy("loadFailed")}</p>
              <button type="button" className="lt-btn" onClick={() => load()}>
                {copy("retry")}
              </button>
            </div>
          ) : (
            <MyLetters
              letters={allLetters}
              limit={limit}
              notice={notice}
              copy={copy}
              onNew={startNewLetter}
              onOpen={openFromList}
              onDelete={(letter) => setDialog({ kind: "delete", letter })}
            />
          )}
        </div>
      )}

      {view.name === "studio" && (
        <div className="lt-scroll">
          <Studio
            key={view.start.letter?.id || "new"}
            start={view.start}
            copy={copy}
            onBack={goToList}
            onSealed={async () => {
              showNotice(copy("sealedNotice"));
              await goToList();
            }}
            onReleased={afterRelease}
            onLimit={() => setDialog({ kind: "limit" })}
          />
        </div>
      )}

      {view.name === "reader" && (
        <div className="lt-scroll">
          <Reader
            letter={view.letter}
            passphrase={view.passphrase}
            copy={copy}
            onBack={goToList}
            onEdit={() => setView({ name: "studio", start: { letter: view.letter, passphrase: view.passphrase } })}
            onReleased={afterRelease}
            onCopyErased={goToList}
          />
        </div>
      )}

      {dialog?.kind === "unlock" && (
        <UnlockDialog copy={copy} onClose={() => setDialog(null)} onUnlock={(pass) => unlock(dialog.letter, pass)} />
      )}
      {dialog?.kind === "delete" && (
        <ConfirmDialog
          copy={copy}
          title={copy("deleteTitle")}
          text={copy("deleteText")}
          confirmLabel={copy("deleteNow")}
          cancelLabel={copy("keepIt")}
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await deleteLetter(dialog.letter.id);
            setDialog(null);
            await load();
          }}
        />
      )}
      {dialog?.kind === "limit" && (
        <Dialog title={copy("limitTitle")} onClose={() => setDialog(null)}>
          <p className="lt-muted">{copy("limitText")}</p>
          <div className="lt-dialog__buttons">
            <button type="button" className="lt-btn lt-btn--text" onClick={() => setDialog(null)}>
              {copy("close")}
            </button>
            <button
              type="button"
              className="lt-btn lt-btn--primary"
              onClick={() => {
                onExit();
                navigate("/account/plans-billing");
              }}
            >
              {copy("seePlans")}
            </button>
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default LetterMode;
